import {useCallback, useEffect, useState} from 'react'
import {type LayoutChangeEvent, View} from 'react-native'
import Animated, {
  Easing,
  FadeInDown,
  FadeOut,
  LinearTransition,
} from 'react-native-reanimated'
import {plural} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'
import {useFocusEffect} from '@react-navigation/native'

import {useHaptics} from '#/lib/haptics'
import {isNetworkError} from '#/lib/strings/errors'
import {logger} from '#/logger'
import {useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {useDialogControl} from '#/components/Dialog'
import {useLink} from '#/components/Link'
import {
  ReportDialog,
  useReportDialogControl,
} from '#/components/moderation/ReportDialog'
import * as Toast from '#/components/Toast'
import {useAnalytics} from '#/analytics'
import {IS_NATIVE} from '#/env'
import {type app} from '#/lexicons'
import {profileHeaderLinkIndex, profileHeaderLinks} from '../header'
import {ProfileLinksChangedError, websiteKey} from '../legacy'
import {logProfileLinkChanges} from '../metrics'
import {PILL_LEFT, PILL_RIGHT_TEXT, PILL_VERTICAL} from '../pillSize'
import {getLinkHost, getSupportProvider} from '../providers'
import {buildProfileRow, GERM_KEY} from '../row'
import {
  useCanEditProfileLinks,
  useOwnProfileLinksQuery,
  useProfileViewLinks,
  useSaveProfileLinksMutation,
} from '../state'
import {type OwnProfileLinks, type ProfileLink} from '../types'
import {LinkFormDialog} from './LinkFormDialog'
import {LinkHoverPreview} from './LinkHoverPreview'
import {ProfileLinkPill} from './LinkPill'
import {LinkPreviewSheet} from './LinkPreviewSheet'

/** The row shows at most this many lines before folding into "+N". */
const MAX_ROWS = 2
const GAP = 8
/** Until the "+N" pill has been measured, assume roughly this wide. */
const MORE_PILL_GUESS = 48
const EASE_OUT = Easing.out(Easing.cubic)

/**
 * The row of link pills under the bio, followed by the Germ DM button. Hidden
 * whenever moderation hides the bio.
 *
 * Visitors open a link with a tap and see where it goes on hover or long
 * press. The owner taps an editable link to edit it.
 */
export function ProfileHeaderLinks({
  profile,
  hidden,
  germButton,
}: {
  profile: app.bsky.actor.defs.ProfileViewDetailed
  hidden: boolean
  /** The Germ DM button, when this profile shows one. */
  germButton?: React.ReactElement
}) {
  const ax = useAnalytics()
  const {currentAccount} = useSession()
  const canEdit = useCanEditProfileLinks()
  const isOwnProfile = currentAccount?.did === profile.did
  /* Repo data enables editing, but must not shuffle the row on arrival. */
  const viewLinks = useProfileViewLinks(profile)
  const {data: snapshot} = useOwnProfileLinksQuery(profile.did, {
    enabled: !hidden && isOwnProfile,
  })
  const links = hidden
    ? []
    : isOwnProfile
      ? profileHeaderLinks(viewLinks, snapshot)
      : viewLinks
  const supportLinkCount = links.filter(link =>
    getSupportProvider(link.url),
  ).length

  useEffect(() => {
    if (links.length === 0) return
    ax.metric('profile:links:impression', {
      profileDid: profile.did,
      linkCount: links.length,
      supportLinkCount,
      isOwnProfile,
    })
    // Once per profile and set of links, not on every render.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.did, links.length, supportLinkCount])

  if (links.length === 0) {
    // the header is a column, so a lone button would stretch to full width
    return germButton ? <View style={[a.flex_row]}>{germButton}</View> : null
  }
  return (
    <ProfileLinksRow
      profile={profile}
      links={links}
      germButton={germButton}
      isOwnProfile={isOwnProfile}
      canEdit={isOwnProfile && canEdit && !!snapshot}
      snapshot={isOwnProfile ? snapshot : undefined}
    />
  )
}

function ProfileLinksRow({
  profile,
  links,
  germButton,
  isOwnProfile,
  canEdit,
  snapshot,
}: {
  profile: app.bsky.actor.defs.ProfileViewDetailed
  links: ProfileLink[]
  germButton?: React.ReactElement
  isOwnProfile: boolean
  /** The owner taps an editable link to edit it, instead of opening it. */
  canEdit: boolean
  snapshot?: OwnProfileLinks
}) {
  const {t: l} = useLingui()
  const ax = useAnalytics()
  const {tap: playTapHaptic, longPress: playLongPressHaptic} = useHaptics()
  const previewControl = useDialogControl()
  const reportControl = useReportDialogControl()
  const formControl = useDialogControl()
  const {save} = useSaveProfileLinksMutation()
  const [isSaving, setIsSaving] = useState(false)
  const [activeLink, setActiveLink] = useState<ProfileLink | null>(null)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingSnapshot, setEditingSnapshot] = useState<OwnProfileLinks>()
  const editingLinks = editingSnapshot?.links ?? links
  const [expanded, setExpanded] = useState(false)
  // the same handling as tapping the pill, e.g. bsky.app links open in-app
  const {onPress: followActiveLink} = useLink({
    to: activeLink?.url ?? '',
    displayText: '',
    shouldProxy: true,
  })

  // the profile screen stays mounted elsewhere in the app, so fold back up on leave
  useFocusEffect(
    useCallback(() => {
      return () => setExpanded(false)
    }, []),
  )

  const logClick = (link: ProfileLink) => {
    ax.metric('profile:links:click', {
      profileDid: profile.did,
      linkUri: link.uri,
      domain: getLinkHost(link.url),
      supportProvider: getSupportProvider(link.url)?.name,
      isOwnProfile,
    })
  }

  const showPreview = (link: ProfileLink) => {
    ax.metric('profile:links:preview', {
      profileDid: profile.did,
      linkUri: link.uri,
      domain: getLinkHost(link.url),
      supportProvider: getSupportProvider(link.url)?.name,
      isOwnProfile,
    })
    setActiveLink(link)
    previewControl.open()
  }

  const saveLinks = async (next: ProfileLink[]) => {
    const baseline = editingSnapshot ?? snapshot
    if (!baseline || isSaving) return
    setIsSaving(true)
    try {
      await save(profile, next, baseline)
      logProfileLinkChanges(ax, baseline.links, next)
      setEditingSnapshot(undefined)
      setEditingIndex(null)
    } catch (e) {
      if (!isNetworkError(e) && !(e instanceof ProfileLinksChangedError)) {
        logger.error('Failed to save profile links', {safeMessage: e})
      }
      Toast.show(
        e instanceof ProfileLinksChangedError
          ? l`Your links changed in another app. Please try again.`
          : l`Couldn’t save your links. Please try again.`,
        {
          type: 'error',
        },
      )
    } finally {
      setIsSaving(false)
    }
  }

  const items = buildProfileRow(links, !!germButton).map(item => {
    if (item.type === 'germ') {
      return {key: GERM_KEY, node: germButton}
    }
    const {link} = item
    const index = profileHeaderLinkIndex(link, snapshot)
    const editable =
      isOwnProfile &&
      index >= 0 &&
      (canEdit || link.legacyWebsite !== undefined)
    return {
      key: websiteKey(link.url),
      node: editable ? (
        <ProfileLinkPill
          link={link}
          did={profile.did}
          label={l`Edit link`}
          onPress={
            isSaving
              ? undefined
              : () => {
                  playTapHaptic()
                  setEditingIndex(index)
                  setEditingSnapshot(snapshot)
                  formControl.open()
                }
          }
        />
      ) : (
        <LinkHoverPreview url={link.url}>
          <ProfileLinkPill
            link={link}
            did={profile.did}
            arrow={!isOwnProfile}
            opensLink
            hint={l`Opens ${getLinkHost(link.url)} outside Bluesky`}
            onPress={() => {
              playTapHaptic()
              logClick(link)
            }}
            onLongPress={
              IS_NATIVE
                ? () => {
                    playLongPressHaptic()
                    showPreview(link)
                  }
                : undefined
            }
            a11yActions={{
              accessibilityActions: [
                {name: 'showDetails', label: l`Show link details`},
              ],
              onAccessibilityAction: event => {
                if (event.nativeEvent.actionName === 'showDetails') {
                  showPreview(link)
                }
              },
            }}
          />
        </LinkHoverPreview>
      ),
    }
  })

  return (
    <>
      <CappedPillRow
        items={items}
        expanded={expanded}
        onExpand={() => setExpanded(true)}
      />
      {isOwnProfile && snapshot && (
        <LinkFormDialog
          control={formControl}
          link={editingIndex === null ? null : editingLinks[editingIndex]}
          removeOnly={!canEdit}
          existingUrls={editingLinks
            .filter((_, i) => i !== editingIndex)
            .map(link => link.url)}
          onSave={saved => {
            if (editingIndex === null) return
            void saveLinks(
              editingLinks.map((link, i) =>
                i === editingIndex ? saved : link,
              ),
            )
          }}
          onRemove={() => {
            if (editingIndex === null) return
            void saveLinks(editingLinks.filter((_, i) => i !== editingIndex))
          }}
        />
      )}
      <LinkPreviewSheet
        control={previewControl}
        link={activeLink}
        onOpen={(link, e) => {
          logClick(link)
          followActiveLink(e)
        }}
        onReport={
          // nothing to report on your own profile
          !isOwnProfile && activeLink?.uri && activeLink.cid
            ? () => reportControl.open()
            : undefined
        }
      />
      {activeLink?.uri && activeLink.cid ? (
        <ReportDialog
          control={reportControl}
          // the link was read off a profile view, so these are well-formed
          subject={{
            $type: 'app.bsky.actor.defs#profileLinkView',
            uri: activeLink.uri as app.bsky.actor.defs.ProfileLinkView['uri'],
            cid: activeLink.cid,
            url: activeLink.url as app.bsky.actor.defs.ProfileLinkView['url'],
          }}
        />
      ) : null}
    </>
  )
}

/**
 * A wrapping row that shows at most `MAX_ROWS` lines. It measures each pill,
 * replays the wrap to see which line each lands on, and folds the rest into a
 * "+N" pill, dropping pills off the last line until that pill fits too.
 * Folded pills stay mounted out of flow so their sizes are known on expand.
 */
function CappedPillRow({
  items,
  expanded,
  onExpand,
}: {
  items: {key: string; node: React.ReactNode}[]
  expanded: boolean
  onExpand: () => void
}) {
  const [width, setWidth] = useState(0)
  const [sizes, setSizes] = useState<Record<string, number>>({})
  const [moreWidth, setMoreWidth] = useState(0)

  const measured = width > 0 && items.every(item => sizes[item.key] != null)

  // what the folded row shows; null means everything fits
  let folded: Set<string> | null = null
  if (measured) {
    const rows: string[][] = [[]]
    let x = 0
    for (const item of items) {
      const w = sizes[item.key]
      if (x > 0 && x + w > width) {
        rows.push([])
        x = 0
      }
      rows[rows.length - 1].push(item.key)
      x += w + GAP
    }
    if (rows.length > MAX_ROWS) {
      const kept = rows.slice(0, MAX_ROWS)
      const last = kept[MAX_ROWS - 1]
      const pill = moreWidth || MORE_PILL_GUESS
      const used = () => last.reduce((sum, key) => sum + sizes[key] + GAP, 0)
      while (last.length && used() + pill > width) last.pop()
      folded = new Set(kept.flat())
    }
  }
  const hiddenCount = folded && !expanded ? items.length - folded.size : 0

  const measure = (key: string) => (e: LayoutChangeEvent) => {
    const w = e.nativeEvent.layout.width
    setSizes(prev => (prev[key] === w ? prev : {...prev, [key]: w}))
  }

  let revealIndex = 0
  return (
    <Animated.View
      // on Android, swipes between the pills must fall through to scroll the profile
      pointerEvents="box-none"
      layout={LinearTransition.duration(300).easing(EASE_OUT)}
      onLayout={e => setWidth(e.nativeEvent.layout.width)}
      style={[
        a.flex_row,
        a.flex_wrap,
        a.align_center,
        {columnGap: GAP, rowGap: GAP},
        // one frame of measuring before anything shows, instead of a reflow
        !measured && {opacity: 0},
      ]}>
      {items.map(item => {
        const inFold = folded ? folded.has(item.key) : true
        if (!inFold && !expanded) {
          // a different key from the shown version, so revealing it animates in
          return (
            <View
              key={`${item.key}:measure`}
              onLayout={measure(item.key)}
              pointerEvents="none"
              style={[a.absolute, {left: 0, top: 0, opacity: 0}]}>
              {item.node}
            </View>
          )
        }
        const entering = !inFold
          ? FadeInDown.duration(320)
              .easing(EASE_OUT)
              .delay(revealIndex++ * 40)
          : undefined
        return (
          <Animated.View
            key={item.key}
            entering={entering}
            layout={LinearTransition.duration(300).easing(EASE_OUT)}
            onLayout={measure(item.key)}>
            {item.node}
          </Animated.View>
        )
      })}
      {hiddenCount > 0 ? (
        <Animated.View
          exiting={FadeOut.duration(120)}
          layout={LinearTransition.duration(300).easing(EASE_OUT)}
          onLayout={e => setMoreWidth(e.nativeEvent.layout.width)}>
          <MorePill count={hiddenCount} onPress={onExpand} />
        </Animated.View>
      ) : null}
    </Animated.View>
  )
}

function MorePill({count, onPress}: {count: number; onPress: () => void}) {
  const t = useTheme()
  return (
    <Button
      testID="profileLinksMoreBtn"
      label={plural(count, {
        one: 'Show # more link',
        other: 'Show # more links',
      })}
      size="small"
      color="secondary"
      onPress={onPress}
      style={{
        paddingVertical: PILL_VERTICAL,
        paddingLeft: PILL_LEFT,
        paddingRight: PILL_RIGHT_TEXT,
      }}>
      <ButtonText style={t.atoms.text}>+{count}</ButtonText>
    </Button>
  )
}
